import {Box, capitalize, IconButton, Popover, Typography} from '@mui/material';
import {TuneOutlined} from '@mui/icons-material';
import {FC, useEffect, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {EditIcon} from 'src/components/icons/EditIcon';
import {ConfigurationForm} from '../ConfigurationForm';
import {
    actionsSx,
    badgeSx,
    booleanPillSx,
    descriptionSx,
    editButtonSx,
    formWrapperSx,
    headerSx,
    titleSx,
    titleWrapSx,
    valuePillSx,
    wrapperSx,
} from './ConfigurationsItem.sx';
import {ConfigurationsItemType} from './ConfigurationsItem.types';
import {useCan} from 'src/permissions/hooks';

export const ConfigurationsItem: FC<ConfigurationsItemType> = ({data}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    // Editing a key is an OCPP ChangeConfiguration on the device, which is its own permission.
    const canConfigure = useCan('chargers.configure');
    const open = Boolean(anchorEl);
    const [value, setValue] = useState<string | boolean | number>('');

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        if (+data.value) {
            setValue(+data.value);
            return;
        }
        setValue(data.value);
    }, [data]);

    const capitalized = capitalize(data.value);
    const isTrue = capitalized === 'True';
    const isFalse = capitalized === 'False';
    const isBoolean = isTrue || isFalse;

    return (
        <Box sx={wrapperSx}>
            <Box sx={headerSx}>
                <Box sx={titleWrapSx}>
                    <Box sx={badgeSx}>
                        <TuneOutlined fontSize="small"/>
                    </Box>
                    <Typography
                        sx={titleSx}
                        variant="subtitle1"
                    >
                        {data.key}
                    </Typography>
                </Box>
                <Box sx={actionsSx}>
                    {isBoolean ? (
                        <Box
                            component="span"
                            sx={booleanPillSx(isTrue)}
                        >
                            {isTrue ? (
                                <FormattedMessage id="enable"/>
                            ) : (
                                <FormattedMessage id="disable"/>
                            )}
                        </Box>
                    ) : (
                        <Typography
                            component="span"
                            sx={valuePillSx}
                        >
                            {value}
                        </Typography>
                    )}
                    {!data.readonly && canConfigure && (
                        <IconButton
                            sx={editButtonSx}
                            onClick={handleOpen}
                        >
                            <EditIcon/>
                        </IconButton>
                    )}
                    <Popover
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        disableScrollLock
                        sx={formWrapperSx}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <ConfigurationForm
                            name={data.key}
                            configValue={data.value}
                            handleClose={handleClose}
                            chargerId={data.chargerId}
                        />
                    </Popover>
                </Box>
            </Box>
            {data.description && (
                <Typography
                    sx={descriptionSx}
                    variant="body2"
                >
                    {data.description}
                </Typography>
            )}
        </Box>
    );
};